import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { HelpCircle, X, Send, MessageCircle, Clock, CheckCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' };
};

const statusColors = {
  open: 'text-yellow-400 bg-yellow-500/10',
  responded: 'text-emerald-400 bg-emerald-500/10',
  closed: 'text-gray-400 bg-gray-500/10',
};
const statusLabels = { open: 'Abierto', responded: 'Respondido', closed: 'Cerrado' };
const categoryLabels = { general: 'General', bug: 'Error/Bug', pregunta: 'Pregunta', sugerencia: 'Sugerencia', mejora: 'Mejora' };

export const SupportButton = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('menu'); // menu | new | tickets | detail
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form, setForm] = useState({ category: 'general', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/support/tickets`, { headers: getHeaders() });
      if (res.ok) setTickets(await res.json());
    } catch {}
  }, []);

  useEffect(() => { if (open) fetchTickets(); }, [open, fetchTickets]);

  const submitTicket = async () => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error('Completa el asunto y mensaje');
    setSending(true);
    try {
      const res = await fetch(`${API}/api/support/tickets`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(form) });
      if (res.ok) {
        toast.success('Ticket enviado correctamente');
        setForm({ category: 'general', subject: '', message: '' });
        setView('tickets');
        fetchTickets();
      } else toast.error('Error al enviar');
    } catch { toast.error('Error de conexion'); }
    setSending(false);
  };

  const openDetail = (t) => { setSelectedTicket(t); setView('detail'); };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-50 w-12 h-12 rounded-full bg-[#0070C0] hover:bg-[#005a9e] text-white shadow-lg flex items-center justify-center transition-all hover:scale-110"
        data-testid="support-fab"
        title="Soporte"
      >
        <HelpCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 z-50 w-[380px] max-h-[520px] animate-in slide-in-from-bottom-4 duration-200" data-testid="support-panel">
      <Card className="border-border/50 shadow-2xl bg-card overflow-hidden flex flex-col max-h-[520px]">
        {/* Header */}
        <div className="bg-[#0070C0] px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            {view !== 'menu' && (
              <button onClick={() => setView(view === 'detail' ? 'tickets' : 'menu')} className="text-white/80 hover:text-white" data-testid="support-back-btn">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <HelpCircle className="w-5 h-5 text-white" />
            <span className="text-white font-semibold text-sm">
              {view === 'menu' ? 'Soporte' : view === 'new' ? 'Nuevo Ticket' : view === 'detail' ? 'Detalle' : 'Mis Tickets'}
            </span>
          </div>
          <button onClick={() => { setOpen(false); setView('menu'); }} className="text-white/80 hover:text-white" data-testid="support-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {view === 'menu' && (
            <div className="p-4 space-y-3">
              <p className="text-sm text-muted-foreground">¿En que podemos ayudarte?</p>
              <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => setView('new')} data-testid="support-new-ticket-btn">
                <Send className="w-4 h-4 text-[#0070C0]" /> Enviar un mensaje
              </Button>
              <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => { setView('tickets'); fetchTickets(); }} data-testid="support-my-tickets-btn">
                <MessageCircle className="w-4 h-4 text-[#0070C0]" /> Mis tickets ({tickets.length})
              </Button>
            </div>
          )}

          {view === 'new' && (
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Categoria</label>
                <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="ticket-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Asunto</label>
                <input
                  className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#0070C0]"
                  placeholder="Describe brevemente tu solicitud"
                  value={form.subject}
                  onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                  data-testid="ticket-subject-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Mensaje</label>
                <textarea
                  className="w-full h-28 px-3 py-2 rounded-md border border-border bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#0070C0]"
                  placeholder="Escribe tu mensaje aqui..."
                  value={form.message}
                  onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                  data-testid="ticket-message-input"
                />
              </div>
              <Button className="w-full gap-2 bg-[#0070C0] hover:bg-[#005a9e]" onClick={submitTicket} disabled={sending} data-testid="ticket-submit-btn">
                <Send className="w-4 h-4" /> {sending ? 'Enviando...' : 'Enviar Ticket'}
              </Button>
            </div>
          )}

          {view === 'tickets' && (
            <div className="p-3 space-y-2">
              {tickets.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-8">No tienes tickets aun.</p>
              ) : tickets.map(t => (
                <button
                  key={t.id}
                  className="w-full text-left p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors"
                  onClick={() => openDetail(t)}
                  data-testid={`ticket-item-${t.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium truncate flex-1 mr-2">{t.subject}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium shrink-0 ${statusColors[t.status]}`}>
                      {statusLabels[t.status]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {new Date(t.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{categoryLabels[t.category] || t.category}</span>
                    {t.responses?.length > 0 && <span className="text-emerald-400 text-[10px]">{t.responses.length} respuesta(s)</span>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {view === 'detail' && selectedTicket && (
            <div className="p-4 space-y-3">
              <div>
                <h4 className="font-semibold text-sm">{selectedTicket.subject}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusColors[selectedTicket.status]}`}>
                    {statusLabels[selectedTicket.status]}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(selectedTicket.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>
              {/* User message */}
              <div className="bg-muted/30 rounded-lg p-3 border border-border/30">
                <p className="text-xs text-muted-foreground mb-1">Tu mensaje:</p>
                <p className="text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
              </div>
              {/* Responses */}
              {selectedTicket.responses?.map(r => (
                <div key={r.id} className="bg-emerald-500/5 rounded-lg p-3 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    <span className="text-xs font-medium text-emerald-400">Equipo Baluad</span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                </div>
              ))}
              {(!selectedTicket.responses || selectedTicket.responses.length === 0) && (
                <p className="text-xs text-muted-foreground text-center py-2">Esperando respuesta del equipo...</p>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SupportButton;
